import React, { useState, useEffect } from "react";
import "./App.css";
import Header from "./components/Header";
import SlotGrid from "./components/SlotGrid";
import SignalGenerator from "./components/SignalGenerator";
import ProviderSelector from "./components/ProviderSelector";
import PlatformSelector from "./components/PlatformSelector";
import IntroAnimation from "./components/IntroAnimation";
import StatsPage from "./components/StatsPage";
import { slotsData, providers, platformLinks } from "./data/mockData";
import { Toaster } from 'sonner';

// Calcula usuarios en línea realistas según la hora (zona México)
const calculateRealisticOnline = () => {
  const now = new Date();
  const hours = now.getHours();

  let baseUsers = 150;
  if (hours >= 19 && hours <= 23) baseUsers = 450;
  else if (hours >= 14 && hours <= 18) baseUsers = 320;
  else if (hours >= 10 && hours <= 13) baseUsers = 220;
  else if (hours >= 0 && hours <= 6) baseUsers = 80;

  const variation = baseUsers * 0.15 * (Math.random() - 0.5) * 2;
  return Math.floor(baseUsers + variation);
};

function App() {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSignalGenerator, setShowSignalGenerator] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('pg');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showIntro, setShowIntro] = useState(true);

  // Registrar visita una vez por sesión
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.pathname === '/stats') return;
    try {
      if (!sessionStorage.getItem('ocbet_visit_logged')) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }).catch(() => {});
        sessionStorage.setItem('ocbet_visit_logged', '1');
      }
    } catch (e) { /* ignore */ }
  }, []);

  const [accuracy, setAccuracy] = useState(() => {
    return parseFloat((85 + Math.random() * 10).toFixed(1));
  });
  const [onlineUsers, setOnlineUsers] = useState(calculateRealisticOnline());

  useEffect(() => {
    const interval = setInterval(() => {
      setAccuracy(prev => {
        const change = (Math.random() - 0.5) * 0.8;
        return Math.min(99, Math.max(80, parseFloat((prev + change).toFixed(1))));
      });
      setOnlineUsers(prev => {
        const change = Math.floor((Math.random() - 0.5) * 10);
        const base = calculateRealisticOnline();
        return Math.max(50, prev + change + Math.floor((base - prev) * 0.1));
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentSlots = slotsData[selectedProvider] || [];
  const providerName = providers[selectedProvider] || '';
  const currentPlatformLink = platformLinks[selectedPlatform] || '';

  // Ruta especial /stats
  if (typeof window !== 'undefined' && window.location.pathname === '/stats') {
    return <StatsPage />;
  }

  if (showIntro) return <IntroAnimation onComplete={() => setShowIntro(false)} />;
  if (!selectedPlatform) return (
    <PlatformSelector onPlatformSelect={setSelectedPlatform} />
  );

  return (
    <div className="min-h-screen bg-[#0a0603] text-white flex flex-col">
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: 'rgba(20, 12, 4, 0.96)',
            border: '1px solid rgba(249, 115, 22, 0.35)',
            color: 'white',
          },
        }}
      />
      {/* Fondo animado - OCBET */}
      <div className="fixed inset-0">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 0%, #3a1a05 0%, #1f0f04 35%, #120902 70%, #0a0603 100%)'
        }} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(249,115,22,0.10)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(245,158,11,0.07)_0%,transparent_50%)]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.3) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
        {[...Array(40)].map((_, i) => (
          <div
            key={`dash-star-${i}`}
            className="absolute rounded-full"
            style={{
              width: `${1 + Math.random() * 2}px`,
              height: `${1 + Math.random() * 2}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 3 === 0 ? '#fdba74' : i % 3 === 1 ? '#fbbf24' : '#ffffff',
              boxShadow: '0 0 4px currentColor',
              opacity: 0.3 + Math.random() * 0.4,
              animation: `twinkle-bg ${2 + Math.random() * 3}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 4}s`,
            }}
          />
        ))}
        <style>{`
          @keyframes twinkle-bg {
            0%, 100% { opacity: 0.2; }
            50% { opacity: 0.9; }
          }
        `}</style>
      </div>

      {showSignalGenerator ? (
        <SignalGenerator
          slot={selectedSlot}
          onBack={() => { setShowSignalGenerator(false); setSelectedSlot(null); }}
          platformLink={currentPlatformLink}
        />
      ) : (
        <div className="relative z-10 flex flex-col min-h-screen">
          <Header
            selectedPlatform={selectedPlatform}
            onBackToPlatforms={() => { setSelectedPlatform(null); setSelectedSlot(null); setShowSignalGenerator(false); }}
          />

          {/* Dashboard Hero */}
          <section className="py-6 sm:py-8 px-4 sm:px-6 border-b border-white/5">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/10 border border-orange-500/30 overflow-hidden flex items-center justify-center p-1.5">
                      <img src="/assets/ocbet/ocbet-emblem.png" alt="OCBET" className="w-full h-full object-contain" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-500 border-2 border-[#0a0603] animate-pulse" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">
                      <span className="text-white">OCBET </span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">Señales</span>
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-500">Inteligencia de señales de alta precisión</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center lg:justify-end gap-3">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-medium text-emerald-400">Conectado</span>
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10">
                    <span className="text-xs text-gray-400">Aciertos: </span>
                    <span className="text-xs font-bold text-orange-400">{accuracy}%</span>
                  </div>
                  <div className="px-4 py-2 rounded-lg bg-white/5 border border-white/10">
                    <span className="text-xs text-gray-400">En línea: </span>
                    <span className="text-xs font-bold text-white">{onlineUsers.toLocaleString('es-MX')}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <ProviderSelector selectedProvider={selectedProvider} selectedPlatform={selectedPlatform} onProviderChange={(id) => { setSelectedProvider(id); setSelectedSlot(null); }} />
          <SlotGrid slots={currentSlots} onSlotSelect={(slot) => { setSelectedSlot(slot); setShowSignalGenerator(true); }} selectedSlot={selectedSlot} selectedProvider={selectedProvider} providerName={providerName} />

          {/* Footer - Juego responsable */}
          <footer className="mt-auto border-t border-white/5">
            <div className="py-6 px-4 sm:px-6 bg-white/[0.01]">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
                    <span className="text-lg font-black text-red-400">18+</span>
                    <span className="text-xs text-red-400/80">Solo mayores de edad</span>
                  </div>

                  <div className="text-center sm:text-left max-w-md">
                    <p className="text-[10px] sm:text-xs text-gray-500 leading-relaxed">
                      Juega con responsabilidad. Si sientes que estás perdiendo el control, busca ayuda.
                      <span className="text-orange-400 ml-1">Juega responsable</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="px-3 py-1.5 rounded bg-white/5 border border-white/10">
                      <span className="text-[10px] text-gray-500 font-bold">BetBlocker</span>
                    </div>
                    <div className="px-3 py-1.5 rounded bg-white/5 border border-white/10">
                      <span className="text-[10px] text-gray-500 font-bold">GamCare</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="py-4 px-4 sm:px-6 border-t border-white/5">
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded overflow-hidden border border-orange-500/40 bg-[#0a0603] flex-shrink-0 flex items-center justify-center p-0.5">
                    <img src="/assets/ocbet/ocbet-emblem.png" alt="OCBET" className="w-full h-full object-contain" />
                  </div>
                  <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400 tracking-wider">OCBET</span>
                  <span>© 2026</span>
                </div>
                <div className="flex items-center gap-4">
                </div>
              </div>
            </div>
          </footer>
        </div>
      )}
    </div>
  );
}

export default App;
